import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  makeStyles,
  MobileStepper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { FunctionComponent, useState } from 'react';
import linkedPatientsScreen from '../../../assets/images/connect-linked-patients-screen.png';
import phoneSignUpScreen from '../../../assets/images/myHcpScreen.png';
import progressScreens from '../../../assets/images/progessScreens.png';
import { colors } from '../../../theme/colors';
import { HCP_REFERRAL_COUPON_DISCOUNT_PERCENT } from '../../Auth/PatientThankYouScreen';

type Step = {
  image: string;
  altText: string;
  subTitle: string;
  description: string;
};

const referralSteps: Step[] = [
  {
    image: linkedPatientsScreen,
    altText: 'Connect linked patients page',
    subTitle: '1.',
    description:
      // eslint-disable-next-line max-len
      `Click the Referral Link button to share your unique link, which includes the ${HCP_REFERRAL_COUPON_DISCOUNT_PERCENT} practitioner discount.`,
  },
  {
    image: phoneSignUpScreen,
    altText: 'Nerva sign up screen',
    subTitle: '2.',
    description: 'Patients use the link to consent to sharing their information.',
  },
  {
    image: progressScreens,
    altText: 'Progess screens',
    subTitle: '3.',
    description: 'As patients complete the Nerva program, you can follow their progress here.',
  },
];

type ReferralStepsProps = { expanded: boolean };
export const ReferralSteps: FunctionComponent<ReferralStepsProps> = (props) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const maxSteps = referralSteps.length;

  return (
    <Accordion
      variant="outlined"
      defaultExpanded={props.expanded}
      className={classes.accordion}
      data-testid="referral-steps"
    >
      <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummaryStyle}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          How to link with a patient?
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {isMobile ? (
          <Box paddingTop={5} data-testid="mobile-view">
            <ReferralStep step={referralSteps[activeStep]} />
            <MobileStepper
              steps={maxSteps}
              position="static"
              variant="dots"
              activeStep={activeStep}
              className={classes.stepperContainer}
              backButton={
                <StepperButton label="<" action={handleBack} disabledState={activeStep === 0} />
              }
              nextButton={
                <StepperButton
                  label=">"
                  action={handleNext}
                  disabledState={activeStep === maxSteps - 1}
                />
              }
            />
          </Box>
        ) : (
          <Grid
            container
            item
            justifyContent="space-between"
            spacing={3}
            data-testid="desktop-view"
          >
            {referralSteps.map((referralStep) => {
              return (
                <Grid item xs={4} key={referralStep.subTitle}>
                  <ReferralStep step={referralStep} />
                </Grid>
              );
            })}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

type ReferralStepProps = { step: Step };
const ReferralStep: FunctionComponent<ReferralStepProps> = (props) => {
  const { step } = props;
  const classes = useStyles();

  return (
    <>
      <Box display="flex" paddingBottom="15px" justifyContent="center">
        <img src={step.image} alt={step.altText} height="230" />
      </Box>
      <Box display="flex" justifyContent="center" maxWidth="270px" m="auto">
        <Typography variant="subtitle1" classes={{ root: classes.infoPanelCopyMargin }}>
          <b>{step.subTitle}</b>
        </Typography>
        <Typography variant="subtitle1">{step.description}</Typography>
      </Box>
    </>
  );
};

type StepperButtonProps = {
  action: () => void;
  disabledState: boolean;
  label: string;
};

const StepperButton: FunctionComponent<StepperButtonProps> = (props) => {
  const classes = useStyles();

  return (
    <Button
      size="small"
      variant="contained"
      className={classes.stepperButton}
      color="primary"
      onClick={props.action}
      disabled={props.disabledState}
    >
      {props.label}
    </Button>
  );
};

const useStyles = makeStyles({
  accordion: {
    backgroundColor: colors.xLightGray,
    borderRadius: 6,
    marginTop: 40,
    '&.MuiAccordion-rounded:last-child': {
      borderRadius: 6,
    },
  },
  accordionSummaryStyle: {
    '&.Mui-expanded': {
      marginLeft: 15,
    },
  },
  accordionDetails: {
    justifyContent: 'center',
  },
  infoPanelCopyMargin: {
    marginRight: 10,
  },
  stepperContainer: {
    justifyContent: 'space-evenly',
    background: 'none',
  },
  stepperButton: {
    width: 80,
  },
});
